/**
 * `/activity/beat` — the open tab reports its time every five minutes.
 *
 * `docs/plans/usage-tracking.md`. The accounting is `lib/activity.ts`; this is
 * the wiring: input and visibility events in, a beat out on a timer, another
 * when the tab is hidden, and a last one marked `ended` when it closes.
 *
 * **The closing beat cannot wait for a token.** `pagehide` gives a handler no
 * time to await, and `sendBeacon` cannot carry an `Authorization` header — so
 * the headers of the last beat are kept and reused with `fetch({keepalive})`.
 * They are at most five minutes old, inside a session token's life.
 *
 * Never throws and never retries: a lost beat is five minutes of one user's
 * count, and the next beat carries on from where this one would have.
 */

import { ActivityClock, BEAT_MS, deviceId, mintId, type Totals } from "../lib/activity";
import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

/** How often the clock is advanced while nothing else happens. */
const TICK_MS = 15_000;

/** Input events, collapsed to one clock call a second at most. */
const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;

let stop: (() => void) | null = null;

/**
 * Start reporting for this page load. Idempotent; returns the stopper.
 *
 * Started once the account is known (`App`), so the first beat names it.
 */
export function startActivity(): () => void {
  if (stop) return stop;
  if (!BASE || typeof document === "undefined") return () => {};

  const sessionId = mintId();
  const device = deviceId();
  const clock = new ActivityClock(Date.now(), document.visibilityState === "visible");
  let lastHeaders: Record<string, string> | null = null;
  let lastInputBooked = 0;
  let ended = false;

  // The smallest window this page load has had, each side on its own
  // (plan: "Screen and window") — sampled now, on every resize, and per beat.
  let minW = window.innerWidth;
  let minH = window.innerHeight;
  const onResize = () => {
    if (window.innerWidth > 0) minW = Math.min(minW, window.innerWidth);
    if (window.innerHeight > 0) minH = Math.min(minH, window.innerHeight);
  };

  const url = () => `${BASE}/activity/beat?userId=${encodeURIComponent(currentUserId())}`;
  const body = (t: Totals, end: boolean) => {
    onResize();
    return JSON.stringify({
      sessionId,
      deviceId: device,
      ...t,
      ended: end,
      ...screenAndWindow(minW, minH),
    });
  };

  const send = async (end = false): Promise<void> => {
    const totals = clock.take(Date.now());
    try {
      const headers = { ...(await requestHeaders()), "Content-Type": "application/json" };
      lastHeaders = headers;
      await fetch(url(), { method: "POST", headers, body: body(totals, end), keepalive: end });
    } catch {
      /* the next beat carries on */
    }
  };

  const onInput = () => {
    const now = Date.now();
    if (now - lastInputBooked < 1000) return;
    lastInputBooked = now;
    clock.input(now);
  };

  const onVisibility = () => {
    const visible = document.visibilityState === "visible";
    clock.setVisible(Date.now(), visible);
    // Hidden is often the last moment a phone gives us; report what we have.
    if (!visible) void send();
  };

  const onPageHide = () => {
    if (ended) return;
    ended = true;
    const totals = clock.take(Date.now());
    if (!lastHeaders) return;
    try {
      void fetch(url(), {
        method: "POST",
        headers: lastHeaders,
        body: body(totals, true),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* closing; nothing to do */
    }
  };

  const tick = window.setInterval(() => clock.advance(Date.now()), TICK_MS);
  const beat = window.setInterval(() => void send(), BEAT_MS);
  for (const name of INPUT_EVENTS) window.addEventListener(name, onInput, { passive: true, capture: true });
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("resize", onResize, { passive: true });

  // The first beat opens the session now, so a short visit is still counted
  // and the closing beat has headers to reuse.
  void send();

  stop = () => {
    window.clearInterval(tick);
    window.clearInterval(beat);
    for (const name of INPUT_EVENTS) window.removeEventListener(name, onInput, { capture: true });
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("resize", onResize);
    stop = null;
  };
  return stop;
}

/**
 * The display, the zoom and the smallest window, as the beat carries them. A
 * zero or missing value is left out — the server keeps what it had.
 */
export function screenAndWindow(minW: number, minH: number): Record<string, number> {
  const out: Record<string, number> = {};
  const put = (key: string, v: number | undefined) => {
    if (typeof v === "number" && Number.isFinite(v) && v > 0) out[key] = key === "pixelRatio" ? v : Math.round(v);
  };
  put("screenW", window.screen?.width);
  put("screenH", window.screen?.height);
  put("pixelRatio", window.devicePixelRatio);
  put("windowW", minW);
  put("windowH", minH);
  return out;
}

// ── reads: the admin's Usage tab and the user's own view ─────────────────────

/** One user's totals over the window (`GET /admin/activity`). */
export interface UsageUser {
  userKey: string;
  email: string | null;
  displayName: string | null;
  /** The brand the account belongs to, e.g. `qufolio`. */
  brand: string | null;
  /** ISO time the newest session started; a bare day when only daily totals remain. */
  lastOpened: string | null;
  devices: number;
  sessions: number;
  activeSeconds: number;
  watchingSeconds: number;
  backgroundSeconds: number;
}

export interface UsageDevice {
  deviceId: string;
  browser: string | null;
  os: string | null;
  deviceType: string | null;
  /** City and country only — the address is never kept (owner, 2026-10-20). */
  lastCity: string | null;
  lastCountry: string | null;
  firstSeen: string;
  lastSeen: string;
  sessions: number;
  activeSeconds: number;
  watchingSeconds: number;
  backgroundSeconds: number;
}

export interface UsageSession {
  deviceId: string;
  browser: string | null;
  os: string | null;
  deviceType: string | null;
  city: string | null;
  country: string | null;
  startedAt: string;
  lastBeatAt: string;
  endedAt: string | null;
  activeSeconds: number;
  watchingSeconds: number;
  backgroundSeconds: number;
  /** Null for sessions before 2026-10-31. */
  screenW?: number | null;
  screenH?: number | null;
  pixelRatio?: number | null;
  /** The smallest window the session saw, each side on its own. */
  windowW?: number | null;
  windowH?: number | null;
}

export interface UsageDetail {
  userKey: string;
  devices: UsageDevice[];
  sessions: UsageSession[];
}

/**
 * Reads throw, unlike the beat: an admin looking at an empty table must be able
 * to tell "nobody used the app" from "the server did not answer".
 */
async function read<T>(path: string): Promise<T> {
  if (!BASE) throw new Error("no server configured");
  const sep = path.includes("?") ? "&" : "?";
  const response = await fetch(`${BASE}${path}${sep}userId=${encodeURIComponent(currentUserId())}`, {
    headers: await requestHeaders(),
  });
  if (!response.ok) throw new Error((await response.text()) || `GET ${path} failed (${response.status})`);
  return (await response.json()) as T;
}

export async function fetchUsageUsers(days: number): Promise<UsageUser[]> {
  return (await read<{ users: UsageUser[] }>(`/admin/activity?days=${days}`)).users;
}

export function fetchUsageUser(userKey: string, days: number): Promise<UsageDetail> {
  return read<UsageDetail>(`/admin/activity/${encodeURIComponent(userKey)}?days=${Math.min(days, 90)}`);
}

export function fetchMyUsage(days: number): Promise<UsageDetail> {
  return read<UsageDetail>(`/activity/me?days=${Math.min(days, 90)}`);
}
